import React, { useEffect, useState, useMemo } from 'react';
import debounce from 'lodash/debounce';
import { useApp } from 'src/@carketa/app/AppContext';
import { ReconTemplateData } from 'src/types/recon/reconTemplateTypes';
import { SalesPacketMenuOrder } from 'src/types/organizations/organizations';
import { trpc } from 'src/utils/trpc';
import { OrganizationPermission } from 'src/@carketa/components/control/OrganizationPermission';
import { FEATURE_FLAG } from 'src/server/common/permissions';
import { ConnectDragSource, Dnd, ItemType } from 'src/@carketa/components/dnd/Dnd';
import { ArrowUpDown, Eye, EyeOff, GripHorizontal } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogTitle } from 'src/ui/dialog';
import { Button } from 'src/ui/button';
import { Typography } from 'src/ui/typography';
import { ReorderSideMenuDialog } from './ReorderSideMenuDialog';
import toast from 'react-hot-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/ui/table';
import { Switch } from 'src/ui/switch';
import { Checkbox } from 'src/ui/checkbox';
import { Input } from 'src/ui/input';
import { Label } from 'src/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'src/ui/tooltip';
import MultipleSelector, { Option } from 'src/ui/simple-multi-select';

interface AutocompleteOptionInterface {
  value: string;
  label: string;
}

interface CardItemType extends ItemType, SalesPacketMenuOrder {
  color: string;
}

export const defaultSalesPacketMenu = [
  {
    id: '1',
    text: 'Vehicle Details',
    section: 'vehicle-details',
  },
  {
    id: '2',
    text: 'Carketa Inspection',
    section: 'vehicle-reports',
  },
  {
    id: '3',
    text: 'Carketa Window Sticker',
    section: 'window-sticker',
  },
  {
    id: '4',
    text: 'Checklist Certificate',
    section: 'checklist-certificate',
  },
  {
    id: '5',
    text: 'Transportation',
    section: 'transportation',
  },
  {
    id: '6',
    text: 'Carketa Connect',
    section: 'carketa-connect',
  },
];

// Menus saved before hide/show existed have no `hidden` key, so anything that
// isn't explicitly hidden counts as visible.
export const isSalesPacketMenuItemVisible = (item?: SalesPacketMenuOrder) => item?.hidden !== true;

// Some tabs only ever render on the sales packet when the org holds a feature
// flag. Keyed on `section` because that value is stable — `text` is the
// user-facing label.
const SALES_PACKET_MENU_REQUIRED_FLAGS: Record<string, string> = {
  'carketa-connect': FEATURE_FLAG.carketa_connect,
};

// Whether this org could ever render the tab, regardless of the dealer's
// show/hide choice. A tab the org can't render must not count toward the
// "at least one visible tab" guard, or the packet still lands empty.
export const isSalesPacketMenuItemAvailable = (item: SalesPacketMenuOrder | undefined, feature_flags: string[]) => {
  const required = SALES_PACKET_MENU_REQUIRED_FLAGS[item?.section ?? ''];
  return !required || feature_flags.includes(required);
};

export const SettingsTable = () => {
  const { me, getOrganizationSettings, updateOrganizationSettings } = useApp();
  const organization_settings = getOrganizationSettings();
  // const [emails, setEmails] = useState<string[]>([]);

  const [workflowId, setWorkflowId] = useState<string>('');
  const [selectedCategories, setSeletedCategories] = useState<AutocompleteOptionInterface[]>([]);

  const { data: recon_categories } = trpc.recon_categories.getAllByLoggedInOrg.useQuery(undefined, {
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
  });

  const { data: recon_templates } = trpc.recon_template.getAllByOrganization.useQuery(undefined, {
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
  });

  const [items, setItems] = useState<any>(defaultSalesPacketMenu);

  useEffect(() => {
    if (!organization_settings) return;
    // if (organization_settings?.emailAddresses) {
    //   setEmails(organization_settings?.emailAddresses);
    // }

    if (!!organization_settings?.carketa_connect_workflow)
      setWorkflowId(organization_settings?.carketa_connect_workflow);

    if (!!organization_settings?.carketa_connect_categories) {
      const loadCategories = organization_settings?.carketa_connect_categories?.map((category) => {
        const reconCategory = recon_categories?.find((c) => c.id === category);
        return {
          value: reconCategory?.id ?? '',
          label: reconCategory?.title ?? '',
        };
      });
      setSeletedCategories(loadCategories);
    }

    if (organization_settings?.sales_packet_menu) {
      setItems(organization_settings?.sales_packet_menu);
    } else {
      updateOrganizationSettings('sales_packet_menu', defaultSalesPacketMenu);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization_settings]);

  const handleNeedsAttentionDays = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateOrganizationSettings('needs_attention_days', Number(event.target.value));
  };

  const handleUseKilometers = (checked: boolean) => {
    updateOrganizationSettings('use_kilometers', checked);
  };

  const handleChangeNeedsAttentionExcludeSaturday = (checked: boolean) => {
    updateOrganizationSettings('exclude_saturday_needs_attention_days', checked);
  };

  const handleChangeNeedsAttentionExcludeSunday = (checked: boolean) => {
    updateOrganizationSettings('exclude_sunday_needs_attention_days', checked);
  };

  const handleUsePlaceholderImageIgnore = (checked: boolean) => {
    updateOrganizationSettings('use_ignored_placeholder_image', checked);
  };

  const handlePlaceholderImageIgnore = (event: React.ChangeEvent<HTMLInputElement>) => {
    updateOrganizationSettings('ignored_placeholder_image', event.target.value);
  };

  const onDebounceSave = debounce(handlePlaceholderImageIgnore, 500);

  const categories = useMemo(() => {
    const workflow_template = recon_templates?.find((template) => template?.id === workflowId)
      ?.template as ReconTemplateData;
    const reconCategories = workflow_template?.categories?.map((category) => {
      const reconCategory = recon_categories?.find((c) => c.id === category.id);
      return {
        value: reconCategory?.id ?? '',
        label: reconCategory?.title ?? '',
      };
    });

    return reconCategories;
  }, [recon_templates, workflowId, recon_categories]);

  const handleWorkflowChange = (value: string) => {
    setSeletedCategories([]);
    setWorkflowId(value);

    updateOrganizationSettings('carketa_connect_workflow', value ?? '');
  };

  const handleCategoryChange = (newValue: Option[]) => {
    const values = categories?.reduce((acc, c) => {
      const match = newValue.find((item: AutocompleteOptionInterface) => item.value === c.value);
      if (match) {
        acc.push(match);
      }
      return acc;
    }, [] as AutocompleteOptionInterface[]);

    setSeletedCategories(values ?? []);

    updateOrganizationSettings(
      'carketa_connect_categories',
      values?.map((category) => category?.value)
    );
  };

  const handleChangeRemovePriceOnSalesPacket = (checked: boolean) => {
    updateOrganizationSettings('remove_price_on_sales_packet', checked);
  };

  const [lastTabDialogOpen, setLastTabDialogOpen] = useState(false);

  const orgFeatureFlags = (me?.organization?.feature_flags as string[]) ?? [];

  // Only offer rows for tabs this org can actually render. The excluded
  // entries stay in the saved menu so they return intact if the org later
  // gains the flag — they're just not shown or reorderable here.
  const availableItems = items.filter((item: any) => isSalesPacketMenuItemAvailable(item, orgFeatureFlags));

  const visibleSalesPacketCount = availableItems.filter((item: any) => isSalesPacketMenuItemVisible(item)).length;

  const handleToggleSalesPacketMenuItem = (id: string) => {
    const target = items.find((item: any) => item.id === id);
    // The guard lives here rather than in the click handler so the invariant
    // holds for any caller, not just the eye button.
    if (isSalesPacketMenuItemVisible(target) && visibleSalesPacketCount <= 1) {
      setLastTabDialogOpen(true);
      return;
    }
    const updatedItems = items.map((item: any) =>
      item.id === id ? { ...item, hidden: isSalesPacketMenuItemVisible(item) } : item
    );
    setItems(updatedItems);
    updateOrganizationSettings('sales_packet_menu', updatedItems);
  };

  const [open, setOpen] = useState(false);
  const handleClose = () => {
    setOpen(false);
  };

  const handleSideMenuOrder = (data: any) => {
    updateOrganizationSettings('side_menu_order', data);
    toast.success('Changes saved!', {
      duration: 5000,
    });
  };

  if (!organization_settings) {
    return <>Loading...</>;
  }

  return (
    <TooltipProvider>
      <div className="flex flex-col gap-4">
        <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
          <Table className="min-w-[650px]" aria-label="simple table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/2">Setting</TableHead>
                <TableHead className="w-1/2">Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="w-1/2 font-medium">Set ODO Units</TableCell>
                <TableCell className="w-1/2">
                  <div className="flex items-center gap-2">
                    <Typography variant="body2">Miles</Typography>
                    <Switch
                      color="success"
                      onCheckedChange={handleUseKilometers}
                      checked={organization_settings?.use_kilometers ?? false}
                    />
                    <Typography variant="body2">Kilometers</Typography>
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="w-1/2 font-medium">Placeholder image ignore</TableCell>
                <TableCell className="w-1/2">
                  <div className="flex items-center gap-2">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>
                          <Checkbox
                            checked={Boolean(organization_settings?.use_ignored_placeholder_image) || false}
                            onCheckedChange={(checked) =>
                              handleUsePlaceholderImageIgnore(checked === true)
                            }
                          />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        This will ignore the specified image. It will not be able to be set as default
                        while this is enabled.
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="w-full">
                          <Input
                            label="Image url"
                            size="sm"
                            defaultValue={organization_settings?.ignored_placeholder_image || ''}
                            onChange={onDebounceSave}
                            type="text"
                            disabled={!organization_settings?.use_ignored_placeholder_image}
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>Image that will be ignored</TooltipContent>
                    </Tooltip>
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="w-1/2 font-medium">Side Menu Configuration</TableCell>
                <TableCell className="w-1/2">
                  <Button size="sm" variant="contained" onClick={() => setOpen(true)}>
                    <ArrowUpDown className="mr-[5px] text-white" />
                    Reorder Side Menu
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
        <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
          <Table className="min-w-[650px]" aria-label="simple table">
            <TableHeader>
              <TableRow>
                <TableHead colSpan={2}>
                  <div className="flex flex-col">
                    Adjust Timeframe For Needs Attention
                    <Typography variant="caption">No progress in _ Days</Typography>
                  </div>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell className="w-1/2 font-medium">Set Number of Days</TableCell>
                <TableCell className="w-1/2">
                  <Input
                    label="Number of Days"
                    size="sm"
                    defaultValue={organization_settings?.needs_attention_days ?? 5}
                    onChange={handleNeedsAttentionDays}
                    type="number"
                    min={1}
                    max={365}
                    step={1}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="w-1/2 font-medium">Exclude Saturday</TableCell>
                <TableCell className="w-1/2">
                  <Switch
                    onCheckedChange={handleChangeNeedsAttentionExcludeSaturday}
                    checked={organization_settings?.exclude_saturday_needs_attention_days ?? false}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className="w-1/2 font-medium">Exclude Sunday</TableCell>
                <TableCell className="w-1/2">
                  <Switch
                    onCheckedChange={handleChangeNeedsAttentionExcludeSunday}
                    checked={organization_settings?.exclude_sunday_needs_attention_days ?? false}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
        <OrganizationPermission feature_flag={FEATURE_FLAG.sales_packet}>
          <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
            <Table className="min-w-[650px]" aria-label="simple table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-1/2">Sales and Marketing</TableHead>
                  <TableHead className="w-1/2">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="w-1/2 font-medium">Remove Price on Sales Packet</TableCell>
                  <TableCell className="w-1/2">
                    <Switch
                      onCheckedChange={handleChangeRemovePriceOnSalesPacket}
                      checked={organization_settings?.remove_price_on_sales_packet ?? false}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="w-1/2 font-medium">Reorder Sales Packet Side Menu</TableCell>
                  <TableCell className="w-1/2">
                    <Dnd
                      useDragHandle
                      items={availableItems}
                      setItems={(reordered) => {
                        // Only the available rows are draggable, so the
                        // flag-gated ones have to be put back on save —
                        // dropping them would erase the entry permanently.
                        // Re-insert at their original index so a later flag
                        // grant restores them where the dealer left them
                        // rather than at the bottom.
                        const merged = [...reordered];
                        items.forEach((item: any, index: number) => {
                          if (!isSalesPacketMenuItemAvailable(item, orgFeatureFlags)) {
                            merged.splice(index, 0, item);
                          }
                        });
                        setItems(merged as CardItemType[]);
                        updateOrganizationSettings('sales_packet_menu', merged);
                      }}
                      renderContainer={(items: React.ReactNode) => {
                        return <div>{items}</div>;
                      }}
                      renderItem={(item: ItemType, _index?: number, dragHandleRef?: ConnectDragSource) => {
                        const itemData = item as CardItemType;
                        const isVisible = isSalesPacketMenuItemVisible(itemData);
                        return (
                          <div
                            className="mb-2 flex items-center rounded-md bg-primary px-3 py-2"
                            style={{
                              // Dim rather than recolor — the row text is
                              // white, so a lighter background would leave
                              // hidden rows unreadable.
                              opacity: isVisible ? 1 : 0.55,
                            }}
                          >
                            <div
                              ref={dragHandleRef as any}
                              className="mr-2 cursor-grab text-white"
                            >
                              <GripHorizontal />
                            </div>
                            <Typography variant="body2" className="flex-1 text-white">
                              {itemData.text}
                            </Typography>
                            <button
                              type="button"
                              role="switch"
                              aria-checked={isVisible}
                              aria-label={`Show ${itemData.text} on the sales packet`}
                              // The handler owns the last-visible guard and
                              // raises the dialog itself.
                              onClick={() => handleToggleSalesPacketMenuItem(itemData.id)}
                              className="ml-4 rounded p-1 text-white hover:bg-white/20"
                            >
                              {isVisible ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                            </button>
                          </div>
                        );
                      }}
                    />
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </OrganizationPermission>
        <OrganizationPermission feature_flag={FEATURE_FLAG.carketa_connect}>
          <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
            <Table className="min-w-[650px]" aria-label="simple table">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-1/2">Carketa Connect</TableHead>
                  <TableHead className="w-1/2">Value</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow>
                  <TableCell className="w-1/2 align-top font-medium">
                    Reconditioning (Default Workflow)
                  </TableCell>
                  <TableCell className="w-1/2">
                    <div className="mt-3 space-y-2">
                      <Label>Workflow</Label>
                      <Select
                        value={workflowId || undefined}
                        onValueChange={handleWorkflowChange}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Workflow" />
                        </SelectTrigger>
                        <SelectContent>
                          {recon_templates?.length ? (
                            recon_templates.map((recon) => (
                              <SelectItem key={recon.id} value={recon.id}>
                                {recon?.title}
                              </SelectItem>
                            ))
                          ) : (
                            <SelectItem value="none" disabled>
                              No Recon WorkFlows
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="mt-3 space-y-2">
                      <Label>Steps</Label>
                      <MultipleSelector
                        options={categories ?? []}
                        value={selectedCategories as Option[]}
                        onChange={handleCategoryChange}
                        placeholder="Steps"
                        hidePlaceholderWhenSelected
                      />
                    </div>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </OrganizationPermission>
      </div>
      {open && (
        <ReorderSideMenuDialog
          open={open}
          handleClose={handleClose}
          sideMenuOrder={handleSideMenuOrder}
          sideMenuOrderData={organization_settings?.side_menu_order}
        />
      )}
      <Dialog open={lastTabDialogOpen} onClose={() => setLastTabDialogOpen(false)}>
        <DialogContent>
          <DialogTitle>At least one tab is required</DialogTitle>
          <Typography variant="body1" className="text-muted-foreground mb-4">
            This is the only tab still showing on your sales packet. Hiding it would leave customers with an empty page,
            so at least one tab has to stay visible. Show another tab first, then hide this one.
          </Typography>
          <DialogFooter>
            <Button variant="contained" onClick={() => setLastTabDialogOpen(false)}>
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};
